import type { ClassSummary } from '@school-erp/types';
import { SUBMISSION_STATUSES, type SubmissionStatus } from '@school-erp/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, apiList } from '../../api/http';
import {
  Notice,
  Pager,
  SchoolSelect,
  errorMessage,
  primaryButton,
  secondaryButton,
  selectClass,
} from '../../components/common';
import { TextField } from '../../components/TextField';
import { t, type MessageKey } from '../../i18n';

interface HomeworkRow {
  id: string;
  title: string;
  instructions: string;
  className: string;
  sectionId: string;
  sectionName: string;
  subjectId: string;
  subjectName: string;
  assignedOn: string;
  dueDate: string;
  status: 'published' | 'closed';
  studentCount: number;
  doneCount: number;
}
interface RosterRow {
  studentId: string;
  name: string;
  rollNumber: string | null;
  status: SubmissionStatus;
  feedback: string | null;
}
interface HomeworkDetail extends HomeworkRow {
  roster: RosterRow[];
}
interface Subject {
  id: string;
  name: string;
  isActive: boolean;
}

const today = () => new Date().toLocaleDateString('en-CA');
const inDays = (n: number) => new Date(Date.now() + n * 86_400_000).toLocaleDateString('en-CA');

/** Homework: set it per section and subject, then record who handed it in. */
export function HomeworkPage() {
  const [schoolId, setSchoolId] = useState('');
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <h1 className="text-2xl font-bold">{t('hw.title')}</h1>
      <SchoolSelect value={schoolId} onChange={setSchoolId} />
      {schoolId && <HomeworkWorkspace schoolId={schoolId} />}
    </div>
  );
}

function HomeworkWorkspace({ schoolId }: { schoolId: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const list = useQuery({
    queryKey: ['homework', schoolId, page],
    queryFn: () => apiList<HomeworkRow>(`/schools/${schoolId}/homework`, { query: { page } }),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['homework', schoolId] });

  return (
    <div className="space-y-4">
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('hw.list')}</h2>
        <button type="button" className={primaryButton} onClick={() => setAdding((v) => !v)}>
          {t('hw.add')}
        </button>
      </div>
      {adding && (
        <NewHomework
          schoolId={schoolId}
          onDone={(text) => {
            setAdding(false);
            setMessage({ kind: 'ok', text });
            refresh();
          }}
        />
      )}
      {list.isPending && <p role="status">{t('common.loading')}</p>}
      {list.isError && <Notice kind="error">{errorMessage(list.error)}</Notice>}
      {list.data?.data.length === 0 && <p className="text-slate-600">{t('hw.empty')}</p>}
      {list.data && list.data.data.length > 0 && (
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase text-slate-600">
            <tr>
              <th scope="col" className="p-2">
                {t('hw.col.title')}
              </th>
              <th scope="col" className="p-2">
                {t('hw.col.class')}
              </th>
              <th scope="col" className="p-2">
                {t('hw.col.subject')}
              </th>
              <th scope="col" className="p-2">
                {t('hw.col.due')}
              </th>
              <th scope="col" className="p-2">
                {t('hw.col.done')}
              </th>
              <th scope="col" className="p-2">
                <span className="sr-only">{t('hw.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data.data.map((hw) => (
              <tr key={hw.id} className="border-t border-slate-200">
                <td className="p-2 font-medium">
                  {hw.title}
                  {hw.status === 'closed' && (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-xs">
                      {t('hw.closed')}
                    </span>
                  )}
                </td>
                <td className="p-2">
                  {hw.className} – {hw.sectionName}
                </td>
                <td className="p-2">{hw.subjectName}</td>
                <td
                  className={`p-2 ${hw.dueDate < today() && hw.status === 'published' ? 'text-red-700' : ''}`}
                >
                  {hw.dueDate}
                </td>
                <td className="p-2">
                  {hw.doneCount}/{hw.studentCount}
                </td>
                <td className="p-2">
                  <button
                    type="button"
                    className={secondaryButton}
                    aria-label={`${t('hw.open')}: ${hw.title}`}
                    onClick={() => setOpenId(openId === hw.id ? null : hw.id)}
                  >
                    {openId === hw.id ? t('hw.hide') : t('hw.open')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list.data && <Pager page={page} totalPages={list.data.meta.totalPages} onPage={setPage} />}
      {openId && (
        <HomeworkDetailPanel
          schoolId={schoolId}
          id={openId}
          onChanged={refresh}
          onDeleted={() => {
            setOpenId(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function NewHomework({ schoolId, onDone }: { schoolId: string; onDone: (text: string) => void }) {
  const [sectionId, setSectionId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [dueDate, setDueDate] = useState(inDays(3));
  const [error, setError] = useState<string | null>(null);

  const classes = useQuery({
    queryKey: ['classes', schoolId],
    queryFn: () => api<ClassSummary[]>(`/schools/${schoolId}/classes`),
  });
  const subjects = useQuery({
    queryKey: ['subjects', schoolId],
    queryFn: () => api<Subject[]>(`/schools/${schoolId}/subjects`),
  });
  const create = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/homework`, {
        method: 'POST',
        body: { sectionId, subjectId, title, instructions, dueDate },
      }),
    onSuccess: () => onDone(t('hw.created')),
    onError: (e) => setError(errorMessage(e)),
  });

  return (
    <form
      noValidate
      aria-label={t('hw.add')}
      className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error && (
        <div className="sm:col-span-2">
          <Notice kind="error">{error}</Notice>
        </div>
      )}
      <div>
        <label htmlFor="hw-section" className="block text-sm font-medium">
          {t('hw.section')}
        </label>
        <select
          id="hw-section"
          className={selectClass}
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
        >
          <option value="">{t('hw.choose')}</option>
          {classes.data?.flatMap((c) =>
            c.sections.map((s) => (
              <option key={s.id} value={s.id}>
                {c.name} – {s.name}
              </option>
            )),
          )}
        </select>
      </div>
      <div>
        <label htmlFor="hw-subject" className="block text-sm font-medium">
          {t('hw.subject')}
        </label>
        <select
          id="hw-subject"
          className={selectClass}
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
        >
          <option value="">{t('hw.choose')}</option>
          {subjects.data
            ?.filter((s) => s.isActive)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
      </div>
      <TextField
        label={t('hw.titleLabel')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <TextField
        label={t('hw.dueDate')}
        type="date"
        value={dueDate}
        onChange={(e) => setDueDate(e.target.value)}
      />
      <div className="sm:col-span-2">
        <label htmlFor="hw-instructions" className="block text-sm font-medium">
          {t('hw.instructions')}
        </label>
        <textarea
          id="hw-instructions"
          rows={3}
          className="mt-1 block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <button
          type="submit"
          className={primaryButton}
          disabled={!sectionId || !subjectId || !title.trim() || !dueDate || create.isPending}
        >
          {t('hw.create')}
        </button>
      </div>
    </form>
  );
}

function HomeworkDetailPanel({
  schoolId,
  id,
  onChanged,
  onDeleted,
}: {
  schoolId: string;
  id: string;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [edits, setEdits] = useState<
    Record<string, { status: SubmissionStatus; feedback: string }>
  >({});
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const detail = useQuery({
    queryKey: ['homework', schoolId, 'detail', id],
    queryFn: () => api<HomeworkDetail>(`/schools/${schoolId}/homework/${id}`),
  });
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['homework', schoolId, 'detail', id] });
    onChanged();
  };
  const save = useMutation({
    mutationFn: () =>
      api(`/schools/${schoolId}/homework/${id}/submissions`, {
        method: 'PUT',
        body: {
          records: Object.entries(edits).map(([studentId, e]) => ({
            studentId,
            status: e.status,
            feedback: e.feedback,
          })),
        },
      }),
    onSuccess: () => {
      setEdits({});
      setMessage({ kind: 'ok', text: t('hw.saved') });
      reload();
    },
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const toggle = useMutation({
    mutationFn: (status: 'published' | 'closed') =>
      api(`/schools/${schoolId}/homework/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: reload,
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });
  const remove = useMutation({
    mutationFn: () => api(`/schools/${schoolId}/homework/${id}`, { method: 'DELETE' }),
    onSuccess: onDeleted,
    onError: (e) => setMessage({ kind: 'error', text: errorMessage(e) }),
  });

  if (detail.isPending) return <p role="status">{t('common.loading')}</p>;
  if (detail.isError) return <Notice kind="error">{errorMessage(detail.error)}</Notice>;
  const hw = detail.data;
  const value = (r: RosterRow) =>
    edits[r.studentId] ?? { status: r.status, feedback: r.feedback ?? '' };

  return (
    <section
      aria-label={hw.title}
      className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
    >
      <header>
        <h3 className="text-lg font-semibold">{hw.title}</h3>
        <p className="text-sm text-slate-600">
          {hw.className} – {hw.sectionName} · {hw.subjectName} · {t('hw.dueDate')}: {hw.dueDate}
        </p>
        {hw.instructions && <p className="mt-2 whitespace-pre-wrap text-sm">{hw.instructions}</p>}
      </header>
      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase text-slate-600">
          <tr>
            <th scope="col" className="p-2">
              {t('hw.roll')}
            </th>
            <th scope="col" className="p-2">
              {t('hw.student')}
            </th>
            <th scope="col" className="p-2">
              {t('hw.status')}
            </th>
            <th scope="col" className="p-2">
              {t('hw.feedback')}
            </th>
          </tr>
        </thead>
        <tbody>
          {hw.roster.map((r) => (
            <tr key={r.studentId} className="border-t border-slate-200">
              <td className="p-2">{r.rollNumber ?? ''}</td>
              <td className="p-2">{r.name}</td>
              <td className="p-2">
                <select
                  aria-label={`${t('hw.status')}: ${r.name}`}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                  value={value(r).status}
                  onChange={(e) =>
                    setEdits({
                      ...edits,
                      [r.studentId]: { ...value(r), status: e.target.value as SubmissionStatus },
                    })
                  }
                >
                  {SUBMISSION_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {t(`hw.status.${s}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="p-2">
                <input
                  aria-label={`${t('hw.feedback')}: ${r.name}`}
                  className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                  value={value(r).feedback}
                  onChange={(e) =>
                    setEdits({
                      ...edits,
                      [r.studentId]: { ...value(r), feedback: e.target.value },
                    })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primaryButton}
          disabled={Object.keys(edits).length === 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('hw.save')}
        </button>
        <button
          type="button"
          className={secondaryButton}
          onClick={() => toggle.mutate(hw.status === 'closed' ? 'published' : 'closed')}
        >
          {hw.status === 'closed' ? t('hw.reopen') : t('hw.close')}
        </button>
        <button type="button" className={secondaryButton} onClick={() => remove.mutate()}>
          {t('hw.delete')}
        </button>
      </div>
    </section>
  );
}
